import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { isEmptyInputValue } from '@assetaccountant/core/utils/validation-utils';

export class AbsoluteValidators {
  /**
   * Validates whether a number exceeds the absolute max distance from zero
   *
   * @param max the max distance from zero
   *
   * @returns 'absoluteMax` which is the signed max value
   */
  static max(max: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (isEmptyInputValue(control.value) || isEmptyInputValue(max)) {
        return null;
      }

      if (Math.abs(control.value) > Math.abs(max)) {
        return { absoluteMax: { absoluteMax: max, actual: control.value } };
      }

      return null;
    };
  }

  /**
   * Validates whether a number is less than the absolute min distance from zero
   *
   * @param min the min distance from zero
   *
   * @returns 'absoluteMin` which is the signed min value
   */
  static min(min: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (isEmptyInputValue(control.value) || isEmptyInputValue(min)) {
        return null;
      }

      if (Math.abs(control.value) < Math.abs(min)) {
        return { absoluteMin: { absoluteMin: min, actual: control.value } };
      }

      return null;
    };
  }

  /**
   * Validates whether a number is between two numbers, regardless of their order
   *
   * @param a number 1
   * @param b number 2
   *
   * @returns 'between` with 'betweenMin' and 'betweenMax' values
   */
  static between(a: number, b: number): ValidatorFn {
    const min = a < b ? a : b;
    const max = a > b ? a : b;

    return (control: AbstractControl): ValidationErrors | null => {
      if (isEmptyInputValue(control.value) || isEmptyInputValue(a) || isEmptyInputValue(b)) {
        return null;
      }

      if (control.value < min || control.value > max) {
        return {
          between: {
            betweenMin: min,
            betweenMax: max,
            actual: control.value,
          },
        };
      }

      return null;
    };
  }

  /**
   * Validates whether a number has the correct positive or negative sign
   *
   * @param sameSign a number which has the sign to enforce
   *
   * @returns 'sameSignIsPositive` boolean
   */
  static sameSign(sign: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (isEmptyInputValue(control.value) || isEmptyInputValue(sign)) {
        return null;
      }

      if (sign >= 0 && control.value < 0) {
        return {
          sameSign: {
            sameSignIsPositive: true,
            actualSignIsPositive: false,
            actual: control.value,
          },
        };
      }

      if (sign < 0 && control.value > 0) {
        return {
          sameSign: {
            sameSignIsPositive: false,
            actualSignIsPositive: true,
            actual: control.value,
          },
        };
      }

      return null;
    };
  }
}
